import axios from 'helpers/api';
import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import { storeList, getStoredStoreId, setStoredStoreId } from 'components/common/StoreList';
import OrderStatusSteps from './OrderStatusSteps';
import MaskedInput from 'react-text-mask';
import FormValidations from 'components/FormValidations/FormValidations';

const OrderDetailModal = ({ dataObj, setRefreshOrder, handleClose }) => {
    const data = dataObj;
    const [storeOptions] = useState([{ key: "Select Store", value: "" }, ...storeList]);
    const savedStoreId = getStoredStoreId();
    const defaultStoreOption = data.pickup_store_id
        ? storeOptions.find(store => Number(store.value) === Number(data.pickup_store_id))
        : (storeOptions.find(store => String(store.value) === String(savedStoreId)) || storeOptions[0]);
    const [selectedStoreValue, setSelectedStoreValue] = useState(defaultStoreOption);
    let orderStatusInternal = JSON.parse(localStorage.getItem("order_status_internal"));
    const masters = JSON.parse(localStorage.getItem("masters"));
    const pharmacyAttentionReasons = masters?.pharmacy_attentions || [];
    const callbackReasons = masters?.callback_masters || [];
    const [formData, setFormData] = useState(data || {});
    const [submitErrors, setSubmitErrors] = useState({});
    const [orderStatusList] = useState(orderStatusInternal);
    let orderType = JSON.parse(localStorage.getItem("order_types"))
    const orderTypeOptions = Object.keys(orderType).map((key) => ({
        value: key,
        label: orderType[key]
    }));
    let deliveryType = JSON.parse(localStorage.getItem("delivery_types"))
    const deliveryTypeOptions = [
        { value: "", label: "Select a type" },
        ...Object.keys(deliveryType).map((key) => ({
            value: key,
            label: deliveryType[key]
        }))
    ];
    const isTaskMode = Number(formData.ordertype) === 10;
    const isOpenTask = isTaskMode && Number(formData.orderstatus) === 99;
    const shouldShowCallbackNumber = Number(formData.is_pharmacy_attention) === 1 || Number(formData.is_callback) === 1;

    const getReasonLabel = (reason) => reason.reason || reason.callback_reason || reason.pharmacy_attention_reason || reason.name;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setSubmitErrors((prev) => ({
            ...prev,
            [name]: ""
        }));
    };

    const handleSubmit = async (overrideStatus = null) => {
        const nextErrors = {};

        if (Number(formData.is_pharmacy_attention) === 1 && !formData.pharmacy_attention_id) {
            nextErrors.pharmacy_attention_id = "Pharmacy attention reason is required.";
        }

        if (Number(formData.is_callback) === 1 && !formData.callback_master_id) {
            nextErrors.callback_master_id = "Callback reason is required.";
        }

        if (shouldShowCallbackNumber && formData.callback_number && !FormValidations(formData.callback_number, 4)) {
            nextErrors.callback_number = "Please enter a valid callback number.";
        }

        if (Object.keys(nextErrors).length > 0) {
            setSubmitErrors(nextErrors);
            return;
        }

        let sendingObj = {
            order_id: formData.orderid,
            rx_number: formData.rxnumber,
            quantity: formData.qty,
            delivery_date: formData.deliverydatetime,
            order_type: orderTypeOptions.find((type) => Number(type.value) === Number(formData.ordertype)).value,
            delivery_type: deliveryTypeOptions.find((type) => Number(type.value) === Number(formData.deliveryOn || "1")).value,
            delivery_time: formData.deliverBy,
            is_pa: formData.is_pa,
            is_loansupplies: formData.is_loansupplies,
            note: (Number(formData.is_callback) === 1 || Number(formData.is_pharmacy_attention) === 1) ? (formData.internal_note || "") : "",
            is_billing_issue: formData.is_billing_issue,
            order_status: orderStatusList.find((type) => type.value === parseInt(formData.orderstatus)).value,
            pickup_store_id: ((Number(formData.deliveryOn) === 5) && selectedStoreValue) ? selectedStoreValue.value : 0,
            is_callback: formData.is_callback,
            is_pharmacy_attention: formData.is_pharmacy_attention,
            pharmacy_attention_id: Number(formData.is_pharmacy_attention) === 1 ? formData.pharmacy_attention_id : 0,
            callback_master_id: Number(formData.is_callback) === 1 ? formData.callback_master_id : 0,
            callback_number: shouldShowCallbackNumber ? (formData.callback_number || "") : "",
        }
        let tokenStr = localStorage.getItem("accessToken");
        const { data } = await axios.post(`${process.env.REACT_APP_SERVER_URL}` + "/orders/update-order/", sendingObj, { headers: { 'Authorization': `Bearer ${tokenStr}` } });
        if (data.output.status) {
            toast.success(data.output.message);
            if (setRefreshOrder) {
                setRefreshOrder(prev => !prev);
            }
            handleClose(false);
        } else {
            toast.error(data.output.message);
        }

    };


    return (
        <Card className='mt-2'>
            <Card.Body >
                <OrderStatusSteps orderData={formData} />
                <Form>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Delivery On</Form.Label>
                                <Form.Select
                                    name="deliveryOn"
                                    value={String(formData.deliveryOn)}
                                    onChange={handleInputChange}
                                >
                                    {deliveryTypeOptions?.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Order Type</Form.Label>
                                <Form.Select
                                    name="ordertype"
                                    value={formData.ordertype || '1'}
                                    onChange={handleInputChange}
                                >
                                    {orderTypeOptions.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Form.Group>
                                <Form.Label>Store</Form.Label>
                                <ThemedSelect
                                    name="store"
                                    closeMenuOnSelect={true}
                                    placeholder='Select store'
                                    classNamePrefix="react-select"
                                    options={storeOptions}
                                    value={selectedStoreValue}
                                    getOptionLabel={option => option.key}
                                    getOptionValue={option => option.value}
                                    onChange={(e) => {
                                        setSelectedStoreValue(e);
                                        if (e && e.value !== undefined) {
                                            setStoredStoreId(e.value);
                                        }
                                    }}
                                    isDisabled={Number(formData.deliveryOn) != 5}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Delivery Date</Form.Label>
                                <DatePicker
                                    placeholderText="mm/dd/yy"
                                    className="form-control"

                                    selected={formData.deliverydatetime ? moment(formData.deliverydatetime).format("MM/DD/YYYY") : null}
                                    onChange={(date) =>
                                        handleInputChange({ target: { name: 'deliverydatetime', value: moment(date).format("MM/DD/YYYY") } })
                                    }
                                    dateFormat="MM/dd/yy"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Deliver By</Form.Label>
                                <Form.Control
                                    type="time"
                                    name="deliverBy"
                                    value={formData.deliverBy || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Form.Group>
                                <Form.Label>Drug</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="drug"
                                    value={formData.drug || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Rx Number</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="rxnumber"
                                    value={formData.rxnumber || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Qty</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="qty"
                                    value={formData.qty || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="">
                        <Col md={12}>
                            {!isTaskMode && <Form.Group>
                                <Form.Label>Order Status</Form.Label>
                                <Form.Select
                                    name="orderstatus"
                                    onChange={handleInputChange}
                                    defaultValue={formData.orderstatus}
                                >
                                    {orderStatusList.map((order) => {
                                        return (
                                            <option key={order.value} value={order.value}>
                                                {order.key}
                                            </option>
                                        )
                                    })}
                                </Form.Select>
                            </Form.Group>}
                        </Col>
                    </Row>
                </Form>
            </Card.Body>
            <Card.Footer>
                <div className="d-flex flex-column gap-3">
                    <Row className="align-items-center">
                        <Col md={4}>
                            <Form.Check
                                type="switch"
                                id="is_pharmacy_attention"
                                label="Pharmacy Attention"
                                checked={formData.is_pharmacy_attention}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        is_pharmacy_attention: e.target.checked ? 1 : 0,
                                        pharmacy_attention_id: e.target.checked ? formData.pharmacy_attention_id : "",
                                        is_callback: e.target.checked ? 0 : formData.is_callback,
                                        callback_master_id: e.target.checked ? "" : formData.callback_master_id,
                                        callback_number: e.target.checked ? formData.callback_number : "",
                                    });

                                    setSubmitErrors((prev) => ({
                                        ...prev,
                                        pharmacy_attention_id: "",
                                        callback_master_id: "",
                                        callback_number: ""
                                    }));
                                }}
                            />
                        </Col>

                        {Number(formData.is_pharmacy_attention) === 1 && (
                            <Col md={8}>
                                <Form.Group>
                                    <Form.Select
                                        size='sm'
                                        name="pharmacy_attention_id"
                                        value={formData.pharmacy_attention_id || ""}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select reason</option>

                                        {pharmacyAttentionReasons.map((reason) => (
                                            <option key={reason.id} value={reason.id}>
                                                {getReasonLabel(reason)}
                                            </option>
                                        ))}
                                    </Form.Select>

                                    {submitErrors.pharmacy_attention_id && (
                                        <small className="text-danger">
                                            {submitErrors.pharmacy_attention_id}
                                        </small>
                                    )}
                                </Form.Group>
                            </Col>
                        )}
                    </Row>

                    {/* Callback */}
                    <Row className="align-items-center">
                        <Col md={4}>
                            <Form.Check
                                type="switch"
                                id="is_callback"
                                label="Callback"
                                checked={formData.is_callback}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        is_callback: e.target.checked ? 1 : 0,
                                        callback_master_id: e.target.checked ? formData.callback_master_id : "",
                                        is_pharmacy_attention: e.target.checked ? 0 : formData.is_pharmacy_attention,
                                        pharmacy_attention_id: e.target.checked ? "" : formData.pharmacy_attention_id,
                                        callback_number: e.target.checked ? formData.callback_number : "",
                                    });

                                    setSubmitErrors((prev) => ({
                                        ...prev,
                                        callback_master_id: "",
                                        pharmacy_attention_id: "",
                                        callback_number: ""
                                    }));
                                }}
                            />
                        </Col>

                        {Number(formData.is_callback) === 1 && (
                            <Col md={8}>
                                <Form.Group>
                                    <Form.Select
                                        size='sm'
                                        name="callback_master_id"
                                        value={formData.callback_master_id || ""}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select reason</option>

                                        {callbackReasons.map((reason) => (
                                            <option key={reason.id} value={reason.id}>
                                                {getReasonLabel(reason)}
                                            </option>
                                        ))}
                                    </Form.Select>

                                    {submitErrors.callback_master_id && (
                                        <small className="text-danger">
                                            {submitErrors.callback_master_id}
                                        </small>
                                    )}
                                </Form.Group>
                            </Col>
                        )}
                    </Row>

                    {shouldShowCallbackNumber && (
                        <Row className="mb-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Callback Number</Form.Label>
                                    <MaskedInput
                                        mask={['+', '1', ' ', '(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                                        className={`form-control form-control-sm ${submitErrors.callback_number ? 'is-invalid' : ''}`}
                                        guide={false}
                                        placeholder="+1 (XXX) XXX-XXXX"
                                        name="callback_number"
                                        value={formData.callback_number || ""}
                                        onChange={handleInputChange}
                                    />
                                    {submitErrors.callback_number && (
                                        <small className="text-danger">
                                            {submitErrors.callback_number}
                                        </small>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    {/* Internal Notes */}
                    {(Number(formData.is_pharmacy_attention) === 1 ||
                        Number(formData.is_callback) === 1) && (
                            <Row className="mb-2">
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Internal Note</Form.Label>

                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            name="internal_note"
                                            value={formData.internal_note || ""}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    <Row>
                        <Col md={9}>
                            <div className="d-flex flex-wrap gap-4">

                                <Form.Check
                                    type="switch"
                                    id="is_pa"
                                    label="PA"
                                    checked={formData.is_pa}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            is_pa: e.target.checked ? 1 : 0,
                                        })
                                    }
                                />

                                <Form.Check
                                    type="switch"
                                    id="is_billing_issue"
                                    label="Billing Issue"
                                    checked={formData.is_billing_issue}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            is_billing_issue: e.target.checked ? 1 : 0,
                                        })
                                    }
                                />

                                <Form.Check
                                    type="switch"
                                    id="is_loansupplies"
                                    label="Loan Supplies"
                                    checked={formData.is_loansupplies}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            is_loansupplies: e.target.checked ? 1 : 0,
                                        })
                                    }
                                />

                            </div>
                        </Col>
                        <Col md={3} className="text-end">
                            <Button
                                variant="success"
                                className="px-4"
                                onClick={handleSubmit}
                            >
                                Update
                            </Button>

                        </Col>
                    </Row>
                </div>
            </Card.Footer>
            {dataObj?.documents && dataObj.documents.length > 0 && (
                <div className="mt-3">
                    <h5>Documents:</h5>
                    <div className="document-list">
                        {dataObj.documents.map((document, index) => (
                            <div key={index} className="document-item mb-2">
                                <a
                                    href={document.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="d-flex align-items-center text-decoration-none"
                                >
                                    {document.document_name.toLowerCase().endsWith('.jpg') ||
                                        document.document_name.toLowerCase().endsWith('.png') ||
                                        document.document_name.toLowerCase().endsWith('.jpeg') ? (
                                        <img
                                            src={document.url}
                                            alt={document.document_name}
                                            className="document-thumbnail me-2"
                                            style={{ width: 50, height: 50, objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <FontAwesomeIcon icon="file-alt" className="me-2" style={{ fontSize: '20px' }} />
                                    )}
                                    <span>{document.document_name}</span>
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card >
    );
};

export default OrderDetailModal;

